import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSegment,
  deleteSegment,
  listSegments,
  previewSegment,
  segmentMembers,
  updateSegment,
  SEGMENT_ATTRIBUTES,
  type Segment,
  type SegmentCondition,
  type SegmentMembers,
  type SegmentOp,
  type SegmentRule,
} from "@/api/segments";
import { Badge, Button, Card } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { useLang } from "@/i18n/LanguageContext";

const OP_OPTIONS: { value: SegmentOp; nl: string; en: string; needsValue: boolean; multi?: boolean }[] = [
  { value: "eq", nl: "is", en: "is", needsValue: true },
  { value: "neq", nl: "is niet", en: "is not", needsValue: true },
  { value: "in", nl: "is één van", en: "is one of", needsValue: true, multi: true },
  { value: "contains", nl: "bevat (tag)", en: "contains (tag)", needsValue: true },
  { value: "exists", nl: "heeft waarde", en: "has a value", needsValue: false },
];

type Row = { attribute: string; op: SegmentOp; value: string };
const EMPTY_ROW: Row = { attribute: "department", op: "eq", value: "" };

function buildRule(match: "all" | "any", rows: Row[]): SegmentRule {
  const conditions = rows
    .map((r): SegmentCondition | null => {
      if (r.op === "exists") return { attribute: r.attribute, op: r.op };
      if (r.op === "in") {
        const values = r.value.split(",").map((s) => s.trim()).filter(Boolean);
        return values.length ? { attribute: r.attribute, op: r.op, values } : null;
      }
      return r.value.trim() ? { attribute: r.attribute, op: r.op, value: r.value.trim() } : null;
    })
    .filter((c): c is SegmentCondition => c !== null);
  return { match, conditions };
}

function rowsFromRule(rule: SegmentRule): Row[] {
  if (!rule?.conditions?.length) return [{ ...EMPTY_ROW }];
  return rule.conditions.map((c) => ({
    attribute: c.attribute,
    op: c.op,
    value: c.op === "in" ? (c.values ?? []).join(", ") : (c.value ?? ""),
  }));
}

export function SegmentsPage() {
  const qc = useQueryClient();
  const { t } = useLang();
  const canEdit = useAuthStore((s) => s.user?.role) === "org_admin";
  const segments = useQuery({ queryKey: ["segments"], queryFn: ({ signal }) => listSegments(signal) });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [match, setMatch] = useState<"all" | "any">("all");
  const [rows, setRows] = useState<Row[]>([{ ...EMPTY_ROW }]);
  const [preview, setPreview] = useState<SegmentMembers | null>(null);
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setEditingId(null);
    setName("");
    setDescription("");
    setMatch("all");
    setRows([{ ...EMPTY_ROW }]);
    setPreview(null);
    setError(null);
  }

  function startEdit(s: Segment) {
    setEditingId(s.id);
    setName(s.name);
    setDescription(s.description ?? "");
    setMatch(s.rule?.match ?? "all");
    setRows(rowsFromRule(s.rule));
    setPreview(null);
    setError(null);
  }

  const doPreview = useMutation({
    mutationFn: () => previewSegment(buildRule(match, rows)),
    onSuccess: (res) => { setPreview(res); setError(null); },
    onError: (e) => setError(e instanceof Error ? e.message : t("Preview mislukt", "Preview failed")),
  });

  const save = useMutation({
    mutationFn: () => {
      const input = { name, description: description || null, rule: buildRule(match, rows) };
      return editingId ? updateSegment(editingId, input) : createSegment(input);
    },
    onSuccess: () => {
      resetForm();
      qc.invalidateQueries({ queryKey: ["segments"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : t("Opslaan mislukt", "Could not save")),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSegment(id),
    onSuccess: () => {
      if (editingId) resetForm();
      qc.invalidateQueries({ queryKey: ["segments"] });
    },
  });

  function updateRow(i: number, patch: Partial<Row>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <section>
      <div className="wrap">
        <div className="toolbar">
          <div>
            <p className="eyebrow">{t("Doelgroepen", "Audiences")}</p>
            <h2 style={{ fontSize: 30 }}>{t("Segmenten", "Segments")}</h2>
          </div>
        </div>

        <div className="editor-grid">
          <Card>
            <p className="card-num">{t("BESTAAND", "EXISTING")}</p>
            {segments.isLoading ? (
              <p>{t("Laden…", "Loading…")}</p>
            ) : segments.data && segments.data.length > 0 ? (
              <ul className="list-rows">
                {segments.data.map((s) => (
                  <SegmentRow key={s.id} segment={s} canEdit={canEdit} onEdit={() => startEdit(s)} onDelete={() => remove.mutate(s.id)} />
                ))}
              </ul>
            ) : (
              <p className="no-data-message">{t("Nog geen segmenten.", "No segments yet.")}</p>
            )}
          </Card>

          {canEdit && (
            <Card>
              <p className="card-num">{editingId ? t("SEGMENT BEWERKEN", "EDIT SEGMENT") : t("NIEUW SEGMENT", "NEW SEGMENT")}</p>
              <form onSubmit={onSubmit}>
                {error && <div className="form-error">{error}</div>}
                <div className="field">
                  <label htmlFor="sname">{t("Naam", "Name")}</label>
                  <input id="sname" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("bijv. Monteurs Noord", "e.g. Technicians North")} />
                </div>
                <div className="field">
                  <label htmlFor="sdesc">{t("Omschrijving", "Description")}</label>
                  <input id="sdesc" value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>

                <div className="field">
                  <label htmlFor="match">Match</label>
                  <select id="match" value={match} onChange={(e) => setMatch(e.target.value as "all" | "any")}>
                    <option value="all">{t("Alle voorwaarden (EN)", "All conditions (AND)")}</option>
                    <option value="any">{t("Eén van de voorwaarden (OF)", "Any condition (OR)")}</option>
                  </select>
                </div>

                {rows.map((r, i) => {
                  const op = OP_OPTIONS.find((o) => o.value === r.op)!;
                  return (
                    <div className="cond-row" key={i}>
                      <select value={r.attribute} onChange={(e) => updateRow(i, { attribute: e.target.value })}>
                        {SEGMENT_ATTRIBUTES.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <select value={r.op} onChange={(e) => updateRow(i, { op: e.target.value as SegmentOp })}>
                        {OP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.nl, o.en)}</option>)}
                      </select>
                      <input
                        disabled={!op.needsValue}
                        placeholder={op.multi ? t("Noord, Oost", "North, East") : op.needsValue ? t("waarde", "value") : "—"}
                        value={r.value}
                        onChange={(e) => updateRow(i, { value: e.target.value })}
                      />
                      <button type="button" className="icon-btn" title={t("Verwijderen", "Remove")}
                        onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs))}>×</button>
                    </div>
                  );
                })}
                <Button variant="outline" type="button" onClick={() => setRows((rs) => [...rs, { attribute: "location", op: "eq", value: "" }])} style={{ marginBottom: 16 }}>
                  + {t("Voorwaarde", "Condition")}
                </Button>
                <p style={{ color: "var(--grey-text)", fontSize: 12.5, marginTop: -8, marginBottom: 14 }}>
                  {t("Lege voorwaarden worden genegeerd. Een segment zonder voorwaarden = iedereen.", "Empty conditions are ignored. A segment with no conditions = everyone.")}
                </p>

                <div className="toolbar">
                  <Button type="button" variant="outline" onClick={() => doPreview.mutate()} disabled={doPreview.isPending}>
                    {doPreview.isPending ? t("Bezig…", "Working…") : t("Preview matches", "Preview matches")}
                  </Button>
                  <Button type="submit" disabled={save.isPending}>{save.isPending ? t("Opslaan…", "Saving…") : editingId ? t("Wijzigingen opslaan", "Save changes") : t("Segment opslaan", "Save segment")}</Button>
                  {editingId && <Button type="button" variant="outline" onClick={resetForm}>{t("Annuleren", "Cancel")}</Button>}
                </div>

                {preview && (
                  <div style={{ marginTop: 8 }}>
                    <Badge tone="ok">{preview.count} {t("medewerker(s)", "employee(s)")}</Badge>
                    <ul className="list-rows" style={{ marginTop: 8 }}>
                      {preview.members.map((mem) => (
                        <li key={mem.user_id}>
                          <span>{mem.full_name}</span>
                          <span style={{ color: "var(--grey-text)", fontSize: 13 }}>{[mem.job_title, mem.department, mem.location].filter(Boolean).join(" · ")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </form>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}

function SegmentRow({ segment, canEdit, onEdit, onDelete }: { segment: Segment; canEdit: boolean; onEdit: () => void; onDelete: () => void }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const members = useQuery({
    queryKey: ["segment-members", segment.id],
    queryFn: ({ signal }) => segmentMembers(segment.id, signal),
    enabled: open,
  });
  return (
    <li style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span style={{ display: "flex", flexDirection: "column" }}>
          <strong>{segment.name}</strong>
          {segment.description && <span style={{ color: "var(--grey-text)", fontSize: 13 }}>{segment.description}</span>}
        </span>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button type="button" className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => setOpen((v) => !v)}>
            {open ? t("Verberg", "Hide") : t("Leden", "Members")}
          </button>
          {canEdit && <button type="button" className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 13 }} onClick={onEdit}>{t("Bewerk", "Edit")}</button>}
          {canEdit && <button type="button" className="icon-btn" title={t("Verwijderen", "Delete")} onClick={onDelete}>×</button>}
        </span>
      </div>
      {open && (
        <div>
          {members.isLoading ? (
            <span style={{ color: "var(--grey-text)" }}>{t("Laden…", "Loading…")}</span>
          ) : (
            <Badge tone="ok">{members.data?.count ?? 0} {t("match(es)", "match(es)")}: {members.data?.members.map((m) => m.full_name).join(", ") || "—"}</Badge>
          )}
        </div>
      )}
    </li>
  );
}
